import React, { useEffect, useState } from 'react';
import { Document, Packer, Paragraph, Table, TableCell, TableRow, TextRun, WidthType } from 'docx';
import { saveAs } from 'file-saver';
import { generateRubric, normalizeAiError } from '../services/ai.js';
import { CAPS_GRADES, CAPS_SUBJECTS } from '../utils/capsOptions.js';
import './AIRubricBuilder.css';

const createEmptyRubric = () => ({
  title: '',
  totalMarks: 0,
  criteria: [],
  teacherNotes: []
});

const DEFAULT_LEVELS = [
  { label: 'Needs Support', marks: 1 },
  { label: 'Approaching Standard', marks: 2 },
  { label: 'Meets Standard', marks: 3 },
  { label: 'Exceeds Standard', marks: 4 }
];

export default function AIRubricBuilder({
  defaultGrade = '',
  defaultSubject = '',
  defaultTaskType = '',
  defaultTotalMarks = 50,
  initialRubric,
  onApplyRubric
}) {
  const [form, setForm] = useState({
    grade: defaultGrade,
    subject: defaultSubject,
    taskType: defaultTaskType,
    totalMarks: defaultTotalMarks,
    additionalContext: ''
  });
  const [rubric, setRubric] = useState(initialRubric ?? createEmptyRubric());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [applyBusy, setApplyBusy] = useState(false);

  useEffect(() => {
    setForm((current) => ({
      ...current,
      grade: defaultGrade,
      subject: defaultSubject,
      taskType: defaultTaskType || current.taskType,
      totalMarks: Number(defaultTotalMarks) > 0 ? Number(defaultTotalMarks) : current.totalMarks
    }));
  }, [defaultGrade, defaultSubject, defaultTaskType, defaultTotalMarks]);

  const rubricLevels = buildRubricLevels(rubric);

  const handleExportWord = async () => {
    if (!rubric.criteria.length) {
      return;
    }

    const rubricMatrixRows = [
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph('')] }),
          ...rubricLevels.map((level, index) => new TableCell({
            children: [
              new Paragraph({
                alignment: 'center',
                children: [
                  new TextRun({ text: String(index + 1), bold: true }),
                  new TextRun({ text: `\n${level.label}`, bold: false })
                ]
              })
            ]
          }))
        ]
      }),
      ...rubric.criteria.map((criterion) => new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                alignment: 'center',
                children: [new TextRun({ text: criterion.title || '', bold: true })]
              })
            ]
          }),
          ...rubricLevels.map((level) => {
            const matchingLevel = findMatchingLevel(criterion.levels, level.label, level.marks);
            return new TableCell({
              children: [
                new Paragraph({
                  alignment: 'center',
                  children: [new TextRun(matchingLevel?.descriptor || '')]
                })
              ]
            });
          })
        ]
      }))
    ];

    const marksRows = [
      new TableRow({
        children: [
          new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Criterion', bold: true })] })] }),
          new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Marks', bold: true })] })] })
        ]
      }),
      ...rubric.criteria.map((criterion) => new TableRow({
        children: [
          new TableCell({ children: [new Paragraph(criterion.title || '')] }),
          new TableCell({ children: [new Paragraph(String(criterion.marks ?? 0))] })
        ]
      }))
    ];

    const notes = rubric.teacherNotes.length
      ? rubric.teacherNotes.map((note) => new Paragraph({ text: note, bullet: { level: 0 } }))
      : [new Paragraph('No teacher notes.')];

    const document = new Document({
      sections: [{
        children: [
          new Paragraph({
            children: [
              new TextRun({ text: 'Name ', bold: true }),
              new TextRun('____________________________')
            ]
          }),
          new Paragraph({
            heading: 'Heading1',
            children: [new TextRun(rubric.title || `${form.subject} ${form.taskType} Rubric`)]
          }),
          new Paragraph({
            children: [
              new TextRun({ text: `Grade: ${form.grade || '-'}`, bold: true }),
              new TextRun(`   Subject: ${form.subject || '-'}`),
              new TextRun(`   Total Marks: ${rubric.totalMarks || form.totalMarks || 0}`)
            ]
          }),
          form.additionalContext
            ? new Paragraph({ text: `Context: ${form.additionalContext}` })
            : new Paragraph(''),
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            rows: rubricMatrixRows
          }),
          new Paragraph({ heading: 'Heading2', text: 'Criterion Weighting' }),
          new Table({
            width: { size: 60, type: WidthType.PERCENTAGE },
            rows: marksRows
          }),
          new Paragraph({ heading: 'Heading2', text: 'Teacher Notes' }),
          ...notes
        ]
      }]
    });

    const blob = await Packer.toBlob(document);
    const safeName = String(rubric.title || `${form.subject}-${form.taskType}-rubric`)
      .replace(/[^a-z0-9]+/gi, '_')
      .replace(/^_+|_+$/g, '');
    saveAs(blob, `${safeName || 'marksure_rubric'}.docx`);
  };

  const updateCriterion = (criterionIndex, field, value) => {
    setRubric((current) => ({
      ...current,
      criteria: current.criteria.map((criterion, index) => (
        index !== criterionIndex
          ? criterion
          : {
              ...criterion,
              [field]: field === 'marks' ? Number(value) || 0 : value
            }
      ))
    }));
  };

  const updateLevel = (criterionIndex, levelIndex, field, value) => {
    setRubric((current) => ({
      ...current,
      criteria: current.criteria.map((criterion, currentCriterionIndex) => (
        currentCriterionIndex !== criterionIndex
          ? criterion
          : {
              ...criterion,
              levels: criterion.levels.map((level, currentLevelIndex) => (
                currentLevelIndex !== levelIndex
                  ? level
                  : {
                      ...level,
                      [field]: field === 'marks' ? Number(value) || 0 : value
                    }
              ))
            }
      ))
    }));
  };

  const handleGenerate = async () => {
    setError('');
    setLoading(true);

    try {
      const generated = await generateRubric(form);
      setRubric(generated);
    } catch (requestError) {
      setError(normalizeAiError(requestError));
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!onApplyRubric) {
      return;
    }

    setApplyBusy(true);
    setError('');
    try {
      await onApplyRubric(rubric);
    } catch (applyError) {
      setError(normalizeAiError(applyError));
    } finally {
      setApplyBusy(false);
    }
  };

  return (
    <section className="ai-rubric-builder">
      <div className="ai-rubric-builder__header">
        <div>
          <h3>AI Rubric Builder</h3>
          <p className="ai-rubric-builder__helper">
            Generate a draft rubric, fine-tune the criteria, then apply it as assessment sections in one step.
          </p>
        </div>
      </div>

      <div className="ai-rubric-builder__grid">
        <div className="ai-rubric-builder__field">
          <label htmlFor="ai-rubric-grade">Grade</label>
          <select
            id="ai-rubric-grade"
            value={form.grade}
            onChange={(event) => setForm((current) => ({ ...current, grade: event.target.value }))}
          >
            <option value="">Select grade</option>
            {!CAPS_GRADES.some((grade) => grade.value === form.grade) && form.grade && <option value={form.grade}>{form.grade}</option>}
            {CAPS_GRADES.map((grade) => <option key={grade.value} value={grade.value}>{grade.label}</option>)}
          </select>
        </div>
        <div className="ai-rubric-builder__field">
          <label htmlFor="ai-rubric-subject">Subject</label>
          <select
            id="ai-rubric-subject"
            value={form.subject}
            onChange={(event) => setForm((current) => ({ ...current, subject: event.target.value }))}
          >
            <option value="">Select subject</option>
            {!CAPS_SUBJECTS.includes(form.subject) && form.subject && <option value={form.subject}>{form.subject}</option>}
            {CAPS_SUBJECTS.map((subject) => <option key={subject} value={subject}>{subject}</option>)}
          </select>
        </div>
        <div className="ai-rubric-builder__field">
          <label htmlFor="ai-rubric-task-type">Task Type</label>
          <input
            id="ai-rubric-task-type"
            value={form.taskType}
            onChange={(event) => setForm((current) => ({ ...current, taskType: event.target.value }))}
            placeholder="e.g. Essay"
          />
        </div>
        <div className="ai-rubric-builder__field">
          <label htmlFor="ai-rubric-total-marks">Total Marks</label>
          <input
            id="ai-rubric-total-marks"
            type="number"
            min="1"
            value={form.totalMarks}
            onChange={(event) => setForm((current) => ({ ...current, totalMarks: Number(event.target.value) || 0 }))}
          />
        </div>
        <div className="ai-rubric-builder__field ai-rubric-builder__field--full">
          <label htmlFor="ai-rubric-context">Teacher Context</label>
          <textarea
            id="ai-rubric-context"
            rows={3}
            value={form.additionalContext}
            onChange={(event) => setForm((current) => ({ ...current, additionalContext: event.target.value }))}
            placeholder="Add class context, cognitive level, learning outcomes, CAPS focus, accommodations, or any rubric instructions."
          />
        </div>
      </div>

      <div className="form-actions mt-3">
        <button type="button" className="btn btn-primary" onClick={handleGenerate} disabled={loading}>
          {loading ? 'Generating Rubric...' : 'Generate Rubric'}
        </button>
      </div>

      {error && <div className="ai-rubric-builder__status error">{error}</div>}

      {rubric.criteria.length > 0 && (
        <div className="ai-rubric-builder__criteria">
          <div className="ai-rubric-builder__field">
            <label htmlFor="ai-rubric-title">Rubric Title</label>
            <input
              id="ai-rubric-title"
              value={rubric.title}
              onChange={(event) => setRubric((current) => ({ ...current, title: event.target.value }))}
            />
          </div>

          <div className="ai-rubric-builder__sheet">
            <div className="ai-rubric-builder__sheet-name">
              <strong>Name</strong>
              <span className="ai-rubric-builder__name-line"></span>
            </div>
            <h4 className="ai-rubric-builder__sheet-title">
              {rubric.title || `${form.grade} ${form.subject} ${form.taskType} Rubric`}
            </h4>
            <div className="ai-rubric-builder__matrix-wrap">
              <table className="ai-rubric-builder__matrix">
                <thead>
                  <tr>
                    <th></th>
                    {rubricLevels.map((level, index) => (
                      <th key={`${level.label}-${index}`}>
                        <div className="ai-rubric-builder__matrix-score">{index + 1}</div>
                        <div>{level.label}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rubric.criteria.map((criterion, criterionIndex) => (
                    <tr key={`matrix-${criterion.title}-${criterionIndex}`}>
                      <th>{criterion.title}</th>
                      {rubricLevels.map((level) => {
                        const matchingLevel = findMatchingLevel(criterion.levels, level.label, level.marks);
                        return (
                          <td key={`${criterion.title}-${level.label}`}>
                            {matchingLevel?.descriptor || ''}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {rubric.criteria.map((criterion, criterionIndex) => (
            <article key={`${criterion.title}-${criterionIndex}`} className="ai-rubric-builder__criterion">
              <div className="ai-rubric-builder__criterion-header">
                <div className="ai-rubric-builder__field">
                  <label htmlFor={`criterion-title-${criterionIndex}`}>Criterion</label>
                  <input
                    id={`criterion-title-${criterionIndex}`}
                    value={criterion.title}
                    onChange={(event) => updateCriterion(criterionIndex, 'title', event.target.value)}
                  />
                </div>
                <div className="ai-rubric-builder__field">
                  <label htmlFor={`criterion-marks-${criterionIndex}`}>Marks</label>
                  <input
                    id={`criterion-marks-${criterionIndex}`}
                    type="number"
                    min="0"
                    value={criterion.marks}
                    onChange={(event) => updateCriterion(criterionIndex, 'marks', event.target.value)}
                  />
                </div>
              </div>

              <div className="ai-rubric-builder__field">
                <label htmlFor={`criterion-description-${criterionIndex}`}>Description</label>
                <textarea
                  id={`criterion-description-${criterionIndex}`}
                  rows={2}
                  value={criterion.description}
                  onChange={(event) => updateCriterion(criterionIndex, 'description', event.target.value)}
                />
              </div>

              <div className="ai-rubric-builder__levels">
                {rubricLevels.map((level, levelIndex) => {
                  const currentLevel = findMatchingLevel(criterion.levels, level.label, level.marks) || {
                    label: level.label,
                    marks: level.marks,
                    descriptor: ''
                  };
                  return (
                  <div key={`${level.label}-${levelIndex}`} className="ai-rubric-builder__criterion-level">
                    <div className="ai-rubric-builder__field">
                      <label htmlFor={`level-label-${criterionIndex}-${levelIndex}`}>Level</label>
                      <input
                        id={`level-label-${criterionIndex}-${levelIndex}`}
                        value={currentLevel.label}
                        onChange={(event) => updateLevel(criterionIndex, levelIndex, 'label', event.target.value)}
                      />
                    </div>
                    <div className="ai-rubric-builder__field">
                      <label htmlFor={`level-description-${criterionIndex}-${levelIndex}`}>Descriptor</label>
                      <input
                        id={`level-description-${criterionIndex}-${levelIndex}`}
                        value={currentLevel.descriptor}
                        onChange={(event) => updateLevel(criterionIndex, levelIndex, 'descriptor', event.target.value)}
                      />
                    </div>
                    <div className="ai-rubric-builder__field">
                      <label htmlFor={`level-marks-${criterionIndex}-${levelIndex}`}>Marks</label>
                      <input
                        id={`level-marks-${criterionIndex}-${levelIndex}`}
                        type="number"
                        min="0"
                        value={currentLevel.marks}
                        onChange={(event) => updateLevel(criterionIndex, levelIndex, 'marks', event.target.value)}
                      />
                    </div>
                  </div>
                )})}
              </div>
            </article>
          ))}

          <div className="ai-rubric-builder__notes">
            <h4>Teacher Notes</h4>
            {rubric.teacherNotes.map((note, noteIndex) => (
              <div className="ai-rubric-builder__field" key={`teacher-note-${noteIndex}`}>
                <input
                  aria-label={`Teacher note ${noteIndex + 1}`}
                  value={note}
                  onChange={(event) => setRubric((current) => ({
                    ...current,
                    teacherNotes: current.teacherNotes.map((currentNote, currentIndex) => (
                      currentIndex === noteIndex ? event.target.value : currentNote
                    ))
                  }))}
                />
              </div>
            ))}
          </div>

          <div className="ai-rubric-builder__footer">
            <button type="button" className="btn btn-secondary" onClick={handleExportWord}>
              Save as Word
            </button>
            {onApplyRubric && (
              <button type="button" className="btn btn-primary" onClick={handleApply} disabled={applyBusy}>
                {applyBusy ? 'Applying Rubric...' : 'Apply Rubric to Sections'}
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function buildRubricLevels(rubric) {
  const levels = rubric.criteria
    .flatMap((criterion) => Array.isArray(criterion.levels) ? criterion.levels : [])
    .map((level) => ({
      label: level.label || '',
      marks: Number(level.marks) || 0
    }))
    .sort((a, b) => a.marks - b.marks);

  const uniqueLevels = [];
  levels.forEach((level) => {
    if (!uniqueLevels.some((item) => item.label === level.label || item.marks === level.marks)) {
      uniqueLevels.push(level);
    }
  });

  return (uniqueLevels.length >= 4 ? uniqueLevels.slice(0, 4) : DEFAULT_LEVELS).sort((a, b) => a.marks - b.marks);
}

function findMatchingLevel(levels, label, marks) {
  return (levels || []).find((level) => level.label === label)
    || (levels || []).find((level) => Number(level.marks) === Number(marks))
    || null;
}
